import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useAccounts } from "../hooks/accounts";
import { useCashflowPositions, useCashflowSummary, useUpdatePosition } from "../hooks/cashflow";
import type { Sector } from "../lib/accounts";
import type { CashflowKind, CashflowRhythm, PositionDto } from "../lib/cashflow";

// ============== Helpers ==============
const NF_EUR = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
function fmtEur(cents: number): string {
  return NF_EUR.format(cents / 100);
}
function fmtSignedEur(cents: number): string {
  const sign = cents > 0 ? "+" : "";
  return sign + fmtEur(cents);
}
function formatMonth(ym: string): string {
  const parts = ym.split("-").map(Number);
  const y = parts[0] ?? 2000;
  const m = parts[1] ?? 1;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("de-DE", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
function currentYearMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function addYearsToYearMonth(ym: string, years: number): string {
  const [y, m] = ym.split("-").map(Number);
  return `${(y ?? 2000) + years}-${String(m ?? 1).padStart(2, "0")}`;
}
function yearStartFromYearMonth(ym: string): string {
  const [y] = ym.split("-").map(Number);
  return `${y ?? 2000}-01`;
}

type Row = {
  position: PositionDto;
  role: "REGULAR" | "TRANSFER_OUT" | "TRANSFER_IN";
};

type Block = {
  sector: Sector;
  rows: Row[];
  incomeCents: number;
  expenseCents: number;
  transferOutCents: number;
  transferInCents: number;
  savingsCents: number;
};

// ============== Page ==============
export function CashflowList() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const today = currentYearMonth();
  const asOf = params.get("asOf") ?? today;
  const activeParam = params.get("active");
  const filters = {
    active: activeParam === "all" ? undefined : activeParam !== "false",
    kind: (params.get("kind") as CashflowKind | null) ?? undefined,
    sector: (params.get("sector") as Sector | null) ?? undefined,
    asOf,
  };

  const positionsQuery = useCashflowPositions(filters);
  const summaryQuery = useCashflowSummary();
  const accountsQuery = useAccounts({ active: "true" });
  const updatePosition = useUpdatePosition();

  async function saveField(id: string, patch: Record<string, unknown>) {
    try {
      await updatePosition.mutateAsync({ id, input: patch });
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const sectorBlocks: Block[] = useMemo(() => {
    const list = positionsQuery.data?.positions ?? [];
    const collator = new Intl.Collator("de", { sensitivity: "base" });
    const order: Sector[] = ["HAUSHALT", "IMMOBILIE", "UNZUGEORDNET"];
    return order
      .map((sector): Block | null => {
        const regular = list.filter((p) => p.sector === sector && p.kind !== "TRANSFER");
        const transferOut = list.filter((p) => p.kind === "TRANSFER" && p.sector === sector);
        const transferIn = list.filter((p) => p.kind === "TRANSFER" && p.targetSector === sector);
        if (regular.length === 0 && transferOut.length === 0 && transferIn.length === 0) return null;
        const income = regular
          .filter((p) => p.kind === "EINNAHME")
          .reduce((s, p) => s + p.monthlyEquivalentCents, 0);
        const expense = regular
          .filter((p) => p.kind === "AUSGABE")
          .reduce((s, p) => s + p.monthlyEquivalentCents, 0);
        const transferOutCents = transferOut.reduce((s, p) => s + p.monthlyEquivalentCents, 0);
        const transferInCents = transferIn.reduce((s, p) => s + p.monthlyEquivalentCents, 0);
        const savingsCents = regular
          .filter((p) => p.kind === "AUSGABE" && p.countsToSavings)
          .reduce((s, p) => s + p.monthlyEquivalentCents, 0);
        const rows: Row[] = [
          ...regular.map((p) => ({ position: p, role: "REGULAR" as const })),
          ...transferOut.map((p) => ({ position: p, role: "TRANSFER_OUT" as const })),
          ...transferIn.map((p) => ({ position: p, role: "TRANSFER_IN" as const })),
        ];
        rows.sort((a, b) => {
          const rank = (r: Row) =>
            r.role === "TRANSFER_IN"
              ? 2
              : r.role === "TRANSFER_OUT"
                ? 3
                : r.position.kind === "EINNAHME"
                  ? 0
                  : 1;
          const ra = rank(a);
          const rb = rank(b);
          if (ra !== rb) return ra - rb;
          return collator.compare(a.position.name, b.position.name);
        });
        return {
          sector,
          rows,
          incomeCents: income,
          expenseCents: expense,
          transferOutCents,
          transferInCents,
          savingsCents,
        };
      })
      .filter((b): b is Block => b !== null);
  }, [positionsQuery.data]);

  const totalsAll = useMemo(() => {
    const income = sectorBlocks.reduce((s, b) => s + b.incomeCents, 0);
    const expense = sectorBlocks.reduce((s, b) => s + b.expenseCents, 0);
    const savings = sectorBlocks.reduce((s, b) => s + b.savingsCents, 0);
    const transferNet = sectorBlocks.reduce(
      (s, b) => s + b.transferInCents - b.transferOutCents,
      0,
    );
    return { income, expense, savings, transferNet, saldo: income - expense };
  }, [sectorBlocks]);

  // Collapse-State pro Sektor
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    IMMOBILIE: true,
    UNZUGEORDNET: true,
  });

  if (positionsQuery.isLoading) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }

  const positions = positionsQuery.data?.positions ?? [];
  const isEmpty =
    positions.length === 0 && activeParam === null && !filters.kind && !filters.sector;

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_cashflow")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("cashflow.list.title")}</h1>
          <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {positions.length}{" "}
            {t("cashflow.positions_plural", { defaultValue: "Positionen" })} ·{" "}
            {t("cashflow.list.asOf.viewing", { month: formatMonth(asOf) })}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Link
            to="/cashflow/monthly"
            className="text-sm font-medium"
            style={{ color: "var(--accent)" }}
          >
            {t("cashflow.list.link_monthly")} →
          </Link>
          <Link
            to="/cashflow/savings-rate"
            className="text-sm font-medium"
            style={{ color: "var(--accent)" }}
          >
            {t("cashflow.list.link_savings_rate")} →
          </Link>
          <Link
            to="/cashflow/new"
            className="px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            + {t("cashflow.list.new")}
          </Link>
        </div>
      </div>

      {/* Stats Strip — Schnitt aus Smoothed (12-Monats-Schnitt) */}
      {summaryQuery.data && (
        <div
          className="fv-card grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x"
          style={{ borderColor: "var(--border)" }}
        >
          <StatMini
            label={t("cashflow.list.summary.income", { defaultValue: "Einnahmen Plan/Monat" })}
            cents={summaryQuery.data.smoothed.incomeCents}
          />
          <StatMini
            label={t("cashflow.list.summary.expense", { defaultValue: "Ausgaben Plan/Monat" })}
            cents={summaryQuery.data.smoothed.expenseCents}
          />
          <StatMini
            label={t("cashflow.list.summary.savings", { defaultValue: "Sparrate Plan/Monat" })}
            cents={totalsAll.savings}
            tone="accent"
          />
          <StatMini
            label={t("cashflow.list.summary.saldo", { defaultValue: "Saldo Plan/Monat" })}
            cents={summaryQuery.data.smoothed.plannedCents}
            tone={summaryQuery.data.smoothed.plannedCents >= 0 ? "pos" : "neg"}
            emphasis
          />
        </div>
      )}

      {/* Stichtag bar */}
      <div className="fv-card p-3 flex items-center gap-3 flex-wrap">
        <span className="text-xs ml-1" style={{ color: "var(--muted)" }}>
          {t("cashflow.list.filter.asOf")}
        </span>
        <input
          type="month"
          value={asOf}
          onChange={(e) => setParam("asOf", e.target.value || null)}
          className="text-sm px-3 py-1.5 rounded-lg"
          style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            color: "var(--text)",
          }}
        />
        <div className="flex gap-1">
          <QuickChip
            active={asOf === today}
            onClick={() => setParam("asOf", today)}
            label={t("cashflow.list.asOf.today")}
          />
          <QuickChip
            active={asOf === yearStartFromYearMonth(today)}
            onClick={() => setParam("asOf", yearStartFromYearMonth(today))}
            label={t("cashflow.list.asOf.year_start")}
          />
          <QuickChip
            active={asOf === addYearsToYearMonth(today, 1)}
            onClick={() => setParam("asOf", addYearsToYearMonth(today, 1))}
            label={t("cashflow.list.asOf.plus_1y")}
          />
          <QuickChip
            active={asOf === addYearsToYearMonth(today, 5)}
            onClick={() => setParam("asOf", addYearsToYearMonth(today, 5))}
            label={t("cashflow.list.asOf.plus_5y")}
          />
        </div>
        <div className="w-px h-5 mx-1" style={{ background: "var(--border)" }} />
        <FilterLabel>{t("cashflow.list.filter.status")}</FilterLabel>
        <BareSelect
          value={activeParam ?? "true"}
          onChange={(v) => setParam("active", v === "true" ? null : v)}
        >
          <option value="true">{t("cashflow.list.filter.active")}</option>
          <option value="false">{t("cashflow.list.filter.inactive")}</option>
          <option value="all">{t("cashflow.list.filter.all")}</option>
        </BareSelect>
        <FilterLabel>{t("cashflow.list.filter.kind")}</FilterLabel>
        <BareSelect
          value={params.get("kind") ?? ""}
          onChange={(v) => setParam("kind", v || null)}
        >
          <option value="">{t("cashflow.list.filter.all")}</option>
          <option value="EINNAHME">{t("cashflow.kind.EINNAHME")}</option>
          <option value="AUSGABE">{t("cashflow.kind.AUSGABE")}</option>
          <option value="TRANSFER">{t("cashflow.kind.TRANSFER")}</option>
        </BareSelect>
        <FilterLabel>{t("cashflow.list.filter.sector")}</FilterLabel>
        <BareSelect
          value={params.get("sector") ?? ""}
          onChange={(v) => setParam("sector", v || null)}
        >
          <option value="">{t("cashflow.list.filter.all")}</option>
          <option value="HAUSHALT">{t("accounts.sector.HAUSHALT")}</option>
          <option value="IMMOBILIE">{t("accounts.sector.IMMOBILIE")}</option>
          <option value="UNZUGEORDNET">{t("accounts.sector.UNZUGEORDNET")}</option>
        </BareSelect>
      </div>

      {/* Status legend */}
      <div className="flex items-center gap-5 text-xs flex-wrap" style={{ color: "var(--muted)" }}>
        <span className="font-semibold">{t("cashflow.legend_title", { defaultValue: "Status" })}:</span>
        <LegendDot color="var(--pos)" label={t("cashflow.kind.EINNAHME")} />
        <LegendDot color="var(--neg)" label={t("cashflow.kind.AUSGABE")} />
        <LegendDot
          color="#0ea5e9"
          label={t("cashflow.list.transfer.incoming", { defaultValue: "Umbuchung ↓" })}
        />
        <LegendDot
          color="#f59e0b"
          label={t("cashflow.list.transfer.outgoing", { defaultValue: "Umbuchung ↑" })}
        />
        <LegendDot
          color="var(--subtle)"
          label={t("cashflow.inactive_label", { defaultValue: "Inaktiv" })}
        />
      </div>

      {/* Empty / Sectors */}
      {isEmpty ? (
        <div className="fv-card p-10 text-center">
          <p style={{ color: "var(--text)" }} className="mb-4">
            {t("cashflow.list.empty")}
          </p>
          <Link
            to="/cashflow/new"
            className="inline-block px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            {t("cashflow.list.empty_cta")}
          </Link>
        </div>
      ) : (
        <>
          {sectorBlocks.map((block) => (
            <SectorCard
              key={block.sector}
              block={block}
              collapsed={collapsed[block.sector] ?? false}
              onToggle={() =>
                setCollapsed((c) => ({ ...c, [block.sector]: !(c[block.sector] ?? false) }))
              }
              saveField={saveField}
              accounts={accountsQuery.data?.accounts ?? []}
            />
          ))}

          {/* Total */}
          <div
            className="fv-card overflow-hidden"
            style={{
              borderColor: "var(--accent)",
              background: "var(--accent-soft)",
              borderWidth: 2,
            }}
          >
            <div className="flex items-center gap-4 px-5 py-4">
              <div className="w-5" />
              <h3 className="text-base font-bold">
                {t("cashflow.total", { defaultValue: "Gesamt" })}
              </h3>
              <span className="text-xs num" style={{ color: "var(--muted)" }}>
                {positions.length}{" "}
                {t("cashflow.positions_plural", { defaultValue: "Positionen" })}
              </span>
              <div className="flex-1" />
              <div className="flex gap-5 text-right text-xs">
                <SumStat
                  label={t("cashflow.list.summary.income")}
                  value={fmtEur(totalsAll.income)}
                  bold
                />
                <SumStat
                  label={t("cashflow.list.summary.expense")}
                  value={fmtEur(totalsAll.expense)}
                  bold
                />
                <SumStat
                  label={t("cashflow.list.summary.savings")}
                  value={fmtEur(totalsAll.savings)}
                  tone="pos"
                  bold
                />
                <SumStat
                  label={t("cashflow.list.summary.saldo")}
                  value={fmtEur(totalsAll.saldo)}
                  tone={totalsAll.saldo >= 0 ? "pos" : "neg"}
                  bold
                />
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

// ============== Stats Strip ==============
function StatMini({
  label,
  cents,
  emphasis,
  tone,
}: {
  label: string;
  cents: number;
  emphasis?: boolean;
  tone?: "pos" | "neg" | "accent";
}) {
  const color =
    tone === "pos"
      ? "var(--pos)"
      : tone === "neg"
        ? "var(--neg)"
        : tone === "accent"
          ? "var(--accent)"
          : "var(--text)";
  return (
    <div className="p-4" style={{ borderColor: "var(--border)" }}>
      <div className="fv-kpi-label mb-1">{label}</div>
      <div
        className={`${emphasis ? "text-2xl" : "text-xl"} font-bold tracking-tight num`}
        style={{ color }}
      >
        {fmtEur(cents)}
      </div>
    </div>
  );
}

// ============== Quick chips ==============
function QuickChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
      style={
        active
          ? { background: "var(--text)", color: "var(--bg)" }
          : { color: "var(--muted)", background: "transparent" }
      }
    >
      {label}
    </button>
  );
}

function FilterLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs" style={{ color: "var(--muted)" }}>
      {children}
    </span>
  );
}

function BareSelect({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm px-2 py-1.5 rounded-lg"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    >
      {children}
    </select>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{ background: color }}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

function SumStat({
  label,
  value,
  tone,
  bold,
}: {
  label: string;
  value: string;
  tone?: "pos" | "neg";
  bold?: boolean;
}) {
  const color = tone === "pos" ? "var(--pos)" : tone === "neg" ? "var(--neg)" : "var(--text)";
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-wider"
        style={{ color: "var(--muted)" }}
      >
        {label}
      </div>
      <div
        className={`${bold ? "font-bold" : "font-semibold"} num`}
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

// ============== Sector Card ==============
function SectorCard({
  block,
  collapsed,
  onToggle,
  saveField,
  accounts,
}: {
  block: Block;
  collapsed: boolean;
  onToggle: () => void;
  saveField: (id: string, patch: Record<string, unknown>) => Promise<void>;
  accounts: { id: string; name: string }[];
}) {
  const { t } = useTranslation();
  const saldo =
    block.incomeCents - block.expenseCents + block.transferInCents - block.transferOutCents;
  const hasTransfer = block.transferInCents > 0 || block.transferOutCents > 0;

  return (
    <div className="fv-card overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center gap-4 transition-colors hover:bg-[var(--row-hover)] text-left"
        style={{ borderBottom: collapsed ? "none" : "1px solid var(--border)" }}
      >
        <svg
          className="w-4 h-4 shrink-0 transition-transform"
          style={{
            color: "var(--muted)",
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
          }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
        <h3 className="text-base font-semibold">{t(`accounts.sector.${block.sector}`)}</h3>
        <span className="text-xs num" style={{ color: "var(--muted)" }}>
          {block.rows.length}{" "}
          {t("cashflow.positions_plural", { defaultValue: "Positionen" })}
        </span>
        <div className="flex-1" />
        <div className="flex gap-5 text-right text-xs">
          <SumStat label={t("cashflow.list.summary.income")} value={fmtEur(block.incomeCents)} />
          <SumStat
            label={t("cashflow.list.summary.expense")}
            value={fmtEur(block.expenseCents)}
          />
          {hasTransfer && (
            <SumStat
              label={t("cashflow.list.summary.transfer_net")}
              value={fmtSignedEur(block.transferInCents - block.transferOutCents)}
            />
          )}
          {block.savingsCents > 0 && (
            <SumStat
              label={t("cashflow.list.summary.savings")}
              value={fmtEur(block.savingsCents)}
              tone="pos"
            />
          )}
          <SumStat
            label={t("cashflow.list.summary.saldo")}
            value={fmtEur(saldo)}
            tone={saldo >= 0 ? "pos" : "neg"}
          />
        </div>
      </button>

      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs"
                style={{ background: "var(--border-soft)", color: "var(--muted)" }}
              >
                <th className="px-3 py-2 font-semibold">
                  {t("cashflow.list.column.name")}
                </th>
                <th className="px-3 py-2 font-semibold">
                  {t("cashflow.list.column.account")}
                </th>
                <th className="px-3 py-2 font-semibold">
                  {t("cashflow.list.column.sector")}
                </th>
                <th className="px-3 py-2 font-semibold">
                  {t("cashflow.list.column.rhythm")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("cashflow.list.column.amount")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("cashflow.list.column.monthly")}
                </th>
                <th className="px-3 py-2 text-center font-semibold w-16">
                  {t("cashflow.list.column.savings")}
                </th>
                <th className="px-3 py-2 font-semibold">
                  {t("cashflow.list.column.firstDue")}
                </th>
                <th className="px-3 py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row) => (
                <PositionRow
                  key={`${row.role}-${row.position.id}`}
                  row={row}
                  saveField={saveField}
                  accounts={accounts}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============== Position Row ==============
function PositionRow({
  row,
  saveField,
  accounts,
}: {
  row: Row;
  saveField: (id: string, patch: Record<string, unknown>) => Promise<void>;
  accounts: { id: string; name: string }[];
}) {
  const { t } = useTranslation();
  const p = row.position;
  const isTransfer = row.role !== "REGULAR";
  const isTransferIn = row.role === "TRANSFER_IN";

  const dotColor = !p.isActive
    ? "var(--subtle)"
    : isTransfer
      ? isTransferIn
        ? "#0ea5e9"
        : "#f59e0b"
      : p.kind === "EINNAHME"
        ? "var(--pos)"
        : "var(--neg)";

  return (
    <tr
      className="border-b transition-colors"
      style={{
        borderColor: "var(--border-soft)",
        background: isTransfer ? "var(--border-soft)" : "transparent",
      }}
    >
      <td className="px-3 py-2.5">
        <span
          className="inline-block w-2 h-2 rounded-full mr-2"
          style={{ background: dotColor }}
          aria-hidden="true"
        />
        {isTransfer && (
          <span
            className="mr-1 font-mono text-xs"
            style={{ color: isTransferIn ? "#0ea5e9" : "#f59e0b" }}
            title={
              isTransferIn
                ? (t("cashflow.list.transfer.incoming_from", {
                    sector: t(`accounts.sector.${p.sector}`),
                  }) ?? "")
                : (t("cashflow.list.transfer.outgoing_to", {
                    sector: t(`accounts.sector.${p.targetSector ?? "UNZUGEORDNET"}`),
                  }) ?? "")
            }
          >
            {isTransferIn ? "↓" : "↑"}
          </span>
        )}
        {isTransfer ? (
          <Link to={`/cashflow/${p.id}`} style={{ color: "var(--accent)" }}>
            {p.name}
          </Link>
        ) : (
          <EditableText value={p.name} onSave={(next) => saveField(p.id, { name: next })} />
        )}
        {!p.isActive && (
          <span className="ml-2 text-xs" style={{ color: "var(--muted)" }}>
            ({t("cashflow.list.inactive")})
          </span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {isTransfer ? (
          <span style={{ color: "var(--muted)" }}>{p.account?.name ?? "—"}</span>
        ) : (
          <EditableSelect
            value={p.accountId ?? ""}
            onSave={(next) => saveField(p.id, { accountId: next || null })}
            options={[
              { value: "", label: "—" },
              ...accounts.map((a) => ({ value: a.id, label: a.name })),
            ]}
          />
        )}
      </td>
      <td className="px-3 py-2.5">
        {isTransfer ? (
          <span style={{ color: "var(--muted)" }}>
            {t(`accounts.sector.${p.sector}`)}
            <span style={{ color: "var(--subtle)" }}> → </span>
            {t(`accounts.sector.${p.targetSector ?? "UNZUGEORDNET"}`)}
          </span>
        ) : (
          <EditableSelect
            value={p.sector}
            onSave={(next) => saveField(p.id, { sector: next as Sector })}
            options={(["HAUSHALT", "IMMOBILIE", "UNZUGEORDNET"] as Sector[]).map((s) => ({
              value: s,
              label: t(`accounts.sector.${s}`) as string,
            }))}
          />
        )}
      </td>
      <td className="px-3 py-2.5">
        {isTransfer ? (
          <span style={{ color: "var(--muted)" }}>{t(`cashflow.rhythm.${p.rhythm}`)}</span>
        ) : (
          <EditableSelect
            value={p.rhythm}
            onSave={(next) => saveField(p.id, { rhythm: next as CashflowRhythm })}
            options={(
              [
                "MONATLICH",
                "QUARTALSWEISE",
                "HALBJAEHRLICH",
                "JAEHRLICH",
                "EINMALIG",
              ] as CashflowRhythm[]
            ).map((r) => ({
              value: r,
              label: t(`cashflow.rhythm.${r}`) as string,
            }))}
          />
        )}
      </td>
      <td className="px-3 py-2.5 text-right num">
        {isTransfer ? (
          <span>{fmtEur(p.amountCents)}</span>
        ) : (
          <EditableNumber
            valueCents={p.amountCents}
            onSave={(next) => saveField(p.id, { amountCents: next })}
          />
        )}
      </td>
      <td className="px-3 py-2.5 text-right num" style={{ color: "var(--muted)" }}>
        {p.rhythm === "EINMALIG" ? (
          <span title={t("cashflow.list.tooltip.einmalig") ?? ""}>—</span>
        ) : (
          fmtEur(p.monthlyEquivalentCents)
        )}
      </td>
      <td className="px-3 py-2.5 text-center">
        {p.kind === "AUSGABE" ? (
          <input
            type="checkbox"
            checked={p.countsToSavings}
            onChange={(e) => saveField(p.id, { countsToSavings: e.target.checked })}
            title={t("cashflow.list.column.savings_tooltip") ?? undefined}
            className="cursor-pointer accent-[var(--accent)]"
          />
        ) : (
          <span style={{ color: "var(--subtle)" }}>—</span>
        )}
      </td>
      <td className="px-3 py-2.5" style={{ color: "var(--muted)" }}>
        {formatMonth(p.firstDueMonth)}
      </td>
      <td className="px-3 py-2.5">
        <Link
          to={`/cashflow/${p.id}`}
          className="text-xs"
          style={{ color: "var(--muted)" }}
          title={t("cashflow.list.open_detail") ?? ""}
        >
          →
        </Link>
      </td>
    </tr>
  );
}

// ============== Inline edit helpers (same logic, themed) ==============
function EditableText({
  value,
  onSave,
}: {
  value: string;
  onSave: (next: string) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-left rounded px-1 -mx-1 hover:bg-[var(--row-hover)]"
        style={{ color: "var(--accent)" }}
      >
        {value}
      </button>
    );
  }
  return (
    <input
      type="text"
      // biome-ignore lint/a11y/noAutofocus: gewuenschtes Verhalten beim Inline-Edit
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        else if (e.key === "Escape") {
          setDraft(value);
          setEditing(false);
        }
      }}
      onBlur={() => {
        setEditing(false);
        const trimmed = draft.trim();
        if (trimmed.length > 0 && trimmed !== value) void onSave(trimmed);
        else setDraft(value);
      }}
      className="rounded px-1 py-0.5 w-full text-sm"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    />
  );
}

function EditableNumber({
  valueCents,
  onSave,
}: {
  valueCents: number;
  onSave: (nextCents: number) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState((valueCents / 100).toString());
  useEffect(() => {
    setDraft((valueCents / 100).toString());
  }, [valueCents]);
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-right rounded px-1 -mx-1 num hover:bg-[var(--row-hover)]"
      >
        {fmtEur(valueCents)}
      </button>
    );
  }
  return (
    <input
      type="number"
      step="0.01"
      // biome-ignore lint/a11y/noAutofocus: gewuenschtes Verhalten beim Inline-Edit
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        else if (e.key === "Escape") {
          setDraft((valueCents / 100).toString());
          setEditing(false);
        }
      }}
      onBlur={() => {
        setEditing(false);
        const num = Number(draft);
        if (Number.isFinite(num) && num > 0) {
          const nextCents = Math.round(num * 100);
          if (nextCents !== valueCents) void onSave(nextCents);
        } else {
          setDraft((valueCents / 100).toString());
        }
      }}
      className="rounded px-1 py-0.5 w-28 text-sm text-right num"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    />
  );
}

function EditableSelect({
  value,
  options,
  onSave,
}: {
  value: string;
  options: { value: string; label: string }[];
  onSave: (next: string) => void | Promise<void>;
}) {
  return (
    <select
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        if (next !== value) void onSave(next);
      }}
      className="bg-transparent rounded px-1 -mx-1 cursor-pointer hover:bg-[var(--row-hover)]"
      style={{ color: "var(--text)" }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
